import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, Warning as WarningIcon } from '@mui/icons-material';
import { useWhiteLabelHiding } from '../hooks/useWhiteLabelHiding';

interface PlanInactiveModalProps {
  open: boolean;
  onClose: () => void;
  onGoToPlans: () => void;
}

const PlanInactiveModal: React.FC<PlanInactiveModalProps> = ({
  open,
  onClose,
  onGoToPlans
}) => {
  const theme = useTheme();
  const { shouldHidePaymentElements } = useWhiteLabelHiding();

  // Se deve ocultar elementos de pagamento, não renderizar o modal
  if (shouldHidePaymentElements) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'white',
          zIndex: 1,
        }}
      >
        <CloseIcon />
      </IconButton>

      <Box
        sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          zIndex: 0,
        }}
      />

      <DialogTitle
        sx={{
          textAlign: 'center',
          pt: 4,
          pb: 2,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <WarningIcon sx={{ fontSize: 40, mr: 1 }} />
          <Typography variant="h4" component="h2" fontWeight="bold">
            Plano Inativo
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <Typography variant="h6" sx={{ mb: 2, opacity: 0.9 }}>
          Seu plano expirou e precisa ser renovado
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, opacity: 0.8 }}>
          Para continuar utilizando todas as funcionalidades da plataforma,
          é necessário contratar um plano ativo.
        </Typography>

        <Box
          sx={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            p: 3,
            mb: 3,
          }}
        >
          <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
            <strong>Você ainda tem acesso a:</strong>
          </Typography>
          <Box component="ul" sx={{ textAlign: 'left', pl: 2, mb: 2 }}>
            <li>Visualização e gestão de leads</li>
            <li>Histórico de conversas</li>
            <li>Relatórios básicos</li>
          </Box>

          <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
            <strong>Funcionalidades indisponíveis:</strong>
          </Typography>
          <Box component="ul" sx={{ textAlign: 'left', pl: 2 }}>
            <li>Agente de IA no WhatsApp</li>
            <li>Automação de conversas</li>
            <li>Recursos avançados de IA</li>
            <li>Integrações premium</li>
          </Box>
        </Box>

        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          Contrate um plano para desbloquear todas as funcionalidades!
        </Typography>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 4, position: 'relative', zIndex: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="large"
          sx={{
            borderColor: 'rgba(255, 255, 255, 0.3)',
            color: 'white',
            px: 4,
            py: 1.5,
            borderRadius: 2,
            mr: 2,
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
              background: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Continuar com Acesso Limitado
        </Button>
        <Button
          onClick={onGoToPlans}
          variant="contained"
          size="large"
          sx={{
            background: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            px: 4,
            py: 1.5,
            borderRadius: 2,
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.3)',
            },
          }}
        >
          Contratar Plano
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanInactiveModal;
